import { useEffect, useState, useRef, ReactElement } from "react";
import { T } from "react-translator-component";
import {
  Box, Button, FormControl, FormControlLabel,
  Paper, Radio, RadioGroup, TableContainer, TextField, Typography
} from "@mui/material";
import "../../App.css";
import { colors } from "../../common/colors";
import { styles } from "../../pages/styles";
import { useAppDispatch } from "../../redux/store";
import { setErrorObj } from "../redux/errorReducer";
import dayjs from "../../util/dayjs";
import LoadingModal from "../../components/LoadingModal";
import Separator from "../../components/Separator";
import AdminPageFrame from "../components/AdminPageFrame";
import DropDown from "../components/DropDown";
import PostCard from "../components/post/PostCard";
import UserPostsHeader from "../components/post/postCard/UserPostsHeader";
import ReadMore from "../components/ReadMore";
import Term, { TermRef } from "../components/Term";
import apiServices from "../services/apiServices";
import { sortOptions } from "../util/posts";

export default function PostEventList(): ReactElement {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [postReportList, setPostReportList] = useState<any[]>([]);
  const [selectSortOption, setSelectSortOption] = useState<string>(sortOptions[0].value);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const termRef = useRef<TermRef>(null)
  const [isTermSelect, setIsTermSelect] = useState<boolean>(false);

  /* API関連 */
  const [posts, setPosts] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>();
  const [pagination, setPagination] = useState<any>({});
  const [errorResponse, setErrorResponse] = useState<any>();

  const createParams = (page?: number) => {
    const sortOrder = parseSortOrder()
    const params: any = {
      term: searchTerm,
      page: page ?? 1,
      sort_type: sortOrder.sortType,
      order: sortOrder.order,
      per_page: 12
    }

    if (isTermSelect) {
      if (termRef?.current?.validate()) {
        const term = termRef?.current?.getTerm();
        params.start_date = term?.startDate?.format("YYYY-MM-DD")
        params.end_date = term?.endDate?.format("YYYY-MM-DD")
      } else {
        return
      }
    }

    return params
  }

  const getEventPosts = async (params: any) => {
    try {
      setIsLoading(true);
      const res = await apiServices.getEventPosts(params);
      if (params.page === 1) {
        setPosts(res.posts)
      } else {
        setPosts([...posts, ...res.posts])
      }
      setLastParams(params)
      setPagination(res.pagination)
    } catch (error: any) {
      setErrorResponse(error)
    } finally {
      setIsLoading(false);
    }
  }

  const getMyReportList = async () => {
    try {
      setIsLoading(true)
      const res = await apiServices.getMyPostReportList()
      setPostReportList(res.post_reports)
    } catch (error: any) {
      setErrorResponse(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getMyReportList()
    const params = createParams()
    if (params) getEventPosts(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!errorResponse) return
    dispatch(setErrorObj(errorResponse))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorResponse])

  const loadMore = () => {
    if (!pagination?.next_page || pagination?.next_page < 2) return

    const params = { ...lastParams, page: pagination.next_page}
    getEventPosts(params);
  };

  const handleSort = (e: any) => {
    setSelectSortOption(e.target.value);
  };

  const handleChangeSearchTerm = (value: string) => {
    setSearchTerm(value);
  }

  const parseSortOrder = () => {
    const [ type, direction ] = selectSortOption.split("_");
    const sortType = type || "created"
    const orderType = direction || "desc"

    return {
      sortType: sortType,
      order: orderType,
    }
  }

  const handleClickTag = (tag: string) => {
    handleChangeSearchTerm(tag)
  }

  const handleSearch = () => {
    const params = createParams()
    if (params) getEventPosts(params)
  }

  return (
    <AdminPageFrame>
      <LoadingModal visible={isLoading} />

      <TableContainer component={Paper} sx={style.searchContainer}>
        <div style={style.searchTitle}>{T("mng_search_area_title")}</div>
        <Typography style={style.searchSectionName}>
          {T("mng_search_event_term")}
        </Typography>

        <Box sx={ style.settingInputBox}>
          <FormControl component="fieldset" sx={{marginTop: "4px"}}>
            <RadioGroup
              name={`radio2`}
              value={isTermSelect}
              onChange={(e) => setIsTermSelect(e.target.value === "true")}
              row
            >
              <FormControlLabel
                value={false}
                control={ <Radio /> }
                label={T("mng_event_term_all")}
              />
              <FormControlLabel
                value={true}
                control={ <Radio /> }
                label=""
              />
            </RadioGroup>
          </FormControl>

          <div style={style.termBox}>
            <Term
              disabled={!isTermSelect}
              baseDate={dayjs()}
              isSelectDateLimit={false}
              vertical={false}
              ref={termRef}
            />
          </div>
        </Box>

        <div style={style.searchInputBox}>
          <TextField
            placeholder={`${T("mng_search_navigator_events_placeholder")}`}
            value={searchTerm}
            onChange={(e) => handleChangeSearchTerm(e.target.value)}
            style={style.searchInputForm}
          />
        </div>

        <div style={style.selectAndBtnBox}>
          <div style={style.dropDownsBox}>
            <DropDown
              title={T("mng_sort")}
              value={selectSortOption}
              options={sortOptions}
              onChange={handleSort}
              sx={style.w30}
            />
          </div>

          <Box sx={style.btncontainer}>
            <Button
              size={"small"}
              variant="contained"
              sx={style.searchButton}
              onClick={() => handleSearch()}
            >
              {T("mng_search")}
            </Button>
          </Box>
        </div>


      </TableContainer>

      <div style={{...styles.communityContainer, marginTop: 20}}>
        <TableContainer component={Paper} sx={{ minWidth: 650, overflowX: "visible", margin: "20px 0" }}>
          <div aria-label="caption table">
            <div style={style.tableHeader}>
              <div style={style.tableTitle}>
                {T("mng_posts_post_events")}
              </div>
            </div>
            <Separator color={colors.farm_gray100} />
          </div>

          <div style={style.postList}>
            {posts.map((post: any, index: number) => (
              <PostCard
                post={post}
                header={
                  <UserPostsHeader
                    post={post}
                    postReports={postReportList}
                    onChangePostReports={getMyReportList}
                  />
                }
                onClickTag={handleClickTag}
                key={index}
              />
            ))}
          </div>

          <ReadMore open={pagination.next_page > 0} sx={style.paginate} onClick={loadMore} />
        </TableContainer>
      </div>
    </AdminPageFrame>
  );
}

const style = {
  searchContainer: {
    marginBottom: "10px",
    padding: "20px",
  },

  searchTitle: {
    padding: "10px 0",
  },

  dropDownsBox: {
    display: "flex",
    margin: "10px 0 0 10px",
    gap: "20px",
    flex: 1
  },

  searchInputForm: {
    width: "100%"
  },

  searchInputBox: {
    margin: "30px 20px 10px"
  },

  m10: {
    margin: "10px"
  },

  termBox: {
    margin: "2px 0",
  },

  w30: {
    width: "30%"
  },

  tableTitle: {
    fontWeight: 700,
    fontSize: 16,
    lineHeight: "120px",
    paddingLeft: 10,
  },

  paginate: {
    flexDirection: "row" as const,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    width: "100%"
  },

  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0 20px",
  },

  postList: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 20,
  },

  searchSectionName: {
    color: "rgba(0, 0, 0, .6)",
    paddingBottom: 5,
    marginLeft: "20px"
  },

  selectAndBtnBox: {
    margin: "10px",
    display: "flex"
  },

  btncontainer: {
    padding: 0,
    display: "flex",
    alignItems: "flex-end",
  },

  searchButton: {
    margin: 1,
    backgroundColor: colors.farm_blue200,
    color: colors.farm_white,
  },

  settingInputBox: {
    display: "flex",
    alignItems: "center",
    marginLeft: "20px"
  },

}